'use client'

import { useEffect, useState } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { Metadata } from 'next'
import { HeroGeometric } from '@/components/ui/hero-geometric'
import { HeroHeader } from '@/components/hero5-header'
import { FooterSection } from '@/components/landing/FooterSection'
import { CreativePricing } from '@/components/landing/CreativePricing'
import { TestimonialsMarquee } from '@/components/landing/TestimonialsMarquee'
import { Sparkles } from '@/components/landing/Sparkles'
import LogoCloud from '@/components/LogoCloud'
import { FeaturesSectionWithHoverEffects } from '@/components/blocks/feature-section-with-hover-effects'
import { AuthDrawer } from '@/components/auth/AuthDrawer'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { IntegrationsSection } from '@/components/landing/IntegrationsSection'

// Moving metadata to layout.tsx since client components can't export metadata
// export const metadata: Metadata = {
//   title: 'NewsClipping - Media Monitoring & Sentiment Analysis',
//   description: 'Real-time media monitoring, sentiment analysis, and brand mentions tracking across all digital channels.',
// }

export default function MainPage() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [isAuthDrawerOpen, setIsAuthDrawerOpen] = useState(false)
  const [defaultAuthTab, setDefaultAuthTab] = useState<'signin' | 'signup'>('signin')
  const [redirectPath, setRedirectPath] = useState<string | null>(null)
  const [isCheckingAuth, setIsCheckingAuth] = useState(false)
  
  // Immediate auth check on page load - DISABLED to fix redirect loop
  // useEffect(() => {
  //   let mounted = true;
  //   let timeoutId: NodeJS.Timeout | null = null;
    
  //   const immediateAuthCheck = async () => {
  //     setIsCheckingAuth(true);
  //     const openAuth = searchParams.get('openAuth');
      
  //     // Only check if trying to open auth drawer
  //     if (!openAuth) {
  //       setIsCheckingAuth(false);
  //       return;
  //     }
      
  //     // Set a safety timeout to prevent infinite loading
  //     timeoutId = setTimeout(() => {
  //       if (mounted) {
  //         console.log('Auth check timed out, proceeding to auth drawer');
  //         setIsCheckingAuth(false);
  //       }
  //     }, 3000); // 3 second timeout
      
  //     try {
  //       // Check multiple sources for auth status
  //       const hasLocalAuth = 
  //         localStorage.getItem('force_auth_success') === 'true' || 
  //         localStorage.getItem('auth_session_active') === 'true';
          
  //       const hasCookieAuth = 
  //         document.cookie.includes('auth_state=authenticated') || 
  //         document.cookie.includes('force_auth_success=true');
        
  //       if (hasLocalAuth || hasCookieAuth) {
  //         console.log('Auth detected on page load, redirecting to dashboard');
          
  //         // Get redirect path if available
  //         const redirect = searchParams.get('redirect') || '/dashboard';
          
  //         // Force redirect using window.location for maximum compatibility
  //         window.location.href = redirect;
  //         return;
  //       }
        
  //       // Final check with Supabase
  //       try {
  //         console.log('Checking Supabase session');
  //         const supabase = createClientComponentClient();
  //         const { data } = await supabase.auth.getSession();
          
  //         if (data?.session) {
  //           console.log('Active session detected, redirecting to dashboard');
  //           localStorage.setItem('force_auth_success', 'true');
            
  //           // Get redirect path if available
  //           const redirect = searchParams.get('redirect') || '/dashboard';
            
  //           // Force redirect using window.location
  //           window.location.href = redirect;
  //           return;
  //         }
          
  //         console.log('No active session found');
  //       } catch (supabaseError) {
  //         console.error('Supabase check failed:', supabaseError);
  //       }
        
  //       // Clear timeout and set not checking if component is still mounted
  //       if (timeoutId) clearTimeout(timeoutId);
  //       if (mounted) {
  //         console.log('Auth check completed, proceeding to auth drawer');
  //         setIsCheckingAuth(false);
  //       }
  //     } catch (error) {
  //       // Handle any errors
  //       console.error('Error during auth check:', error);
        
  //       // Clear timeout and set not checking if component is still mounted
  //       if (timeoutId) clearTimeout(timeoutId);
  //       if (mounted) {
  //         setIsCheckingAuth(false);
  //       }
  //     }
  //   };
    
  //   immediateAuthCheck();
    
  //   // Cleanup function
  //   return () => {
  //     mounted = false;
  //     if (timeoutId) clearTimeout(timeoutId);
  //   };
  // }, [searchParams]);
  
  // Simplified check for openAuth parameter, no more redirects
  useEffect(() => {
    const openAuth = searchParams.get('openAuth')
    const redirectPath = searchParams.get('redirect')
    
    if (openAuth) {
      // Set the default tab based on the parameter
      if (openAuth === 'signin' || openAuth === 'signup') {
        setDefaultAuthTab(openAuth)
      }
      
      // Open the auth drawer
      setIsAuthDrawerOpen(true)
      
      // If redirectPath is provided, store it for after authentication
      if (redirectPath) {
        // Store in both localStorage and cookie for redundancy
        localStorage.setItem('auth_redirect_path', redirectPath)
        document.cookie = `auth_redirect_path=${redirectPath}; path=/; max-age=3600; SameSite=Lax`
      }
    }
  }, [searchParams])
  
  const handleOpenAuthDrawer = (tab: 'signin' | 'signup') => {
    setDefaultAuthTab(tab)
    setIsAuthDrawerOpen(true)
  }

  // If we're still checking auth status, show minimal content
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative w-full min-h-screen overflow-y-auto text-white bg-[#100D1C]">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.05] via-transparent to-rose-500/[0.05] blur-3xl -z-10" />
      <HeroHeader 
        // Pass down the ability to open the drawer from this component
        openAuthDrawer={handleOpenAuthDrawer}
      />
      <HeroGeometric 
        badge="NewsClipping AI" 
        title1="Monitor Media" 
        title2="Understand Sentiment" 
      />
      
      <LogoCloud />
      
      <section id="features" className="py-16 mt-16 relative z-0">
        <Sparkles className="py-8">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Powerful Features for Complete Media Monitoring
            </h2>
            <div className="bg-transparent rounded-xl p-4 md:p-6 lg:p-8">
              <FeaturesSectionWithHoverEffects />
            </div>
          </div>
        </Sparkles>
      </section>
      
      <IntegrationsSection />
      
      <section id="testimonials" className="py-16 relative z-0">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            What Our Clients Say
          </h2>
        </div>
        <TestimonialsMarquee />
      </section>
      
      <section id="pricing" className="py-16 relative z-0">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Choose Your Plan
          </h2>
          <p className="text-center text-gray-300 max-w-2xl mx-auto mb-12">
            Select the perfect monitoring package for your business needs. All plans include core features with flexible scaling options.
          </p>
        </div>
        <CreativePricing />
      </section>
      
      <section id="footer">
        <FooterSection openAuthDrawer={handleOpenAuthDrawer} />
      </section>
      
      {/* Auth Drawer handled at page level */}
      <AuthDrawer 
        isOpen={isAuthDrawerOpen} 
        onClose={() => setIsAuthDrawerOpen(false)} 
        defaultTab={defaultAuthTab}
      />
    </div>
  )
} 